import { useState, useCallback } from 'react';
import { imageEditorAPI } from '../api/imageEditorApi';

/**
 * Custom hook managing image editor state and operations.
 *
 * State: currentImage, originalImage, activeTool, undoStack, redoStack, loading, error, variants
 * Exposes action dispatchers for every editing operation.
 */
export function useImageEditor() {
  const [currentImage, setCurrentImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [variants, setVariants] = useState([]);

  // --- Core helpers ---

  const loadImage = useCallback((file) => {
    const url = file instanceof Blob ? URL.createObjectURL(file) : file;
    setCurrentImage(url);
    setOriginalImage(url);
    setUndoStack([]);
    setRedoStack([]);
    setError(null);
    setVariants([]);
  }, []);

  const revertToOriginal = useCallback(() => {
    if (!originalImage || originalImage === currentImage) return;
    setUndoStack((prev) => [...prev, currentImage]);
    setRedoStack([]);
    setCurrentImage(originalImage);
  }, [originalImage, currentImage]);

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const stack = [...prev];
      const previous = stack.pop();
      setRedoStack((r) => [...r, currentImage]);
      setCurrentImage(previous);
      return stack;
    });
  }, [currentImage]);

  const redo = useCallback(() => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const stack = [...prev];
      const next = stack.pop();
      setUndoStack((u) => [...u, currentImage]);
      setCurrentImage(next);
      return stack;
    });
  }, [currentImage]);

  /**
   * Convert a blob: URL or data: URL to an actual Blob for upload.
   */
  const _resolveImageSource = useCallback(async (src) => {
    if (src instanceof Blob) return src;
    if (typeof src === 'string' && (src.startsWith('blob:') || src.startsWith('data:'))) {
      const resp = await fetch(src);
      return await resp.blob();
    }
    return src;
  }, []);

  /**
   * Generic operation applier.
   */
  const applyOperation = useCallback(
    async (apiMethod, params = {}) => {
      if (!currentImage) return;
      setLoading(true);
      setError(null);
      try {
        setUndoStack((prev) => [...prev, currentImage]);
        setRedoStack([]);
        const imageSource = await _resolveImageSource(currentImage);
        const response = await apiMethod(imageSource, params);
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        setCurrentImage(url);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Operation failed');
      } finally {
        setLoading(false);
      }
    },
    [currentImage, _resolveImageSource],
  );

  // --- Convenience wrappers ---

  const crop = useCallback(
    (params) => applyOperation(imageEditorAPI.crop, params),
    [applyOperation],
  );

  const resize = useCallback(
    (params) => applyOperation(imageEditorAPI.resize, params),
    [applyOperation],
  );

  const changeQuality = useCallback(
    (params) => applyOperation(imageEditorAPI.changeQuality, params),
    [applyOperation],
  );

  const flip = useCallback(
    (params) => applyOperation(imageEditorAPI.flip, params),
    [applyOperation],
  );

  const brightnessContrast = useCallback(
    (params) => applyOperation(imageEditorAPI.brightnessContrast, params),
    [applyOperation],
  );

  const sharpen = useCallback(
    (params) => applyOperation(imageEditorAPI.sharpen, params),
    [applyOperation],
  );

  const vignette = useCallback(
    (params) => applyOperation(imageEditorAPI.vignette, params),
    [applyOperation],
  );

  const redEye = useCallback(
    (params) => applyOperation(imageEditorAPI.redEye, params),
    [applyOperation],
  );

  const blemishRemove = useCallback(
    (params) => applyOperation(imageEditorAPI.blemishRemove, params),
    [applyOperation],
  );

  const backgroundRemove = useCallback(
    () => applyOperation(imageEditorAPI.backgroundRemove, {}),
    [applyOperation],
  );

  const penToolClip = useCallback(
    (params) => applyOperation(imageEditorAPI.penToolClip, params),
    [applyOperation],
  );

  const skinEnhance = useCallback(
    (params) => applyOperation(imageEditorAPI.skinEnhance, params),
    [applyOperation],
  );

  const colorBalance = useCallback(
    (params) => applyOperation(imageEditorAPI.colorBalance, params),
    [applyOperation],
  );

  const curves = useCallback(
    (params) => applyOperation(imageEditorAPI.curves, params),
    [applyOperation],
  );

  // --- Special operations ---

  const skinEnhanceVariants = useCallback(async () => {
    if (!currentImage) return;
    setLoading(true);
    setError(null);
    try {
      const imageSource = await _resolveImageSource(currentImage);
      const response = await imageEditorAPI.skinEnhanceVariants(imageSource);
      setVariants(response.data.variants || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load variants');
    } finally {
      setLoading(false);
    }
  }, [currentImage, _resolveImageSource]);

  const save = useCallback(
    async (params = {}) => {
      if (!currentImage) return null;
      setLoading(true);
      setError(null);
      try {
        const imageSource = await _resolveImageSource(currentImage);
        const response = await imageEditorAPI.save(imageSource, params);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Save failed');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [currentImage, _resolveImageSource],
  );

  const download = useCallback(async () => {
    if (!currentImage) return;
    setLoading(true);
    setError(null);
    try {
      const imageSource = await _resolveImageSource(currentImage);
      const response = await imageEditorAPI.download(imageSource);
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'edited-image.png';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Download failed');
    } finally {
      setLoading(false);
    }
  }, [currentImage, _resolveImageSource]);

  return {
    // State
    currentImage,
    originalImage,
    activeTool,
    undoStack,
    redoStack,
    loading,
    error,
    variants,

    // Core actions
    loadImage,
    setActiveTool,
    undo,
    redo,
    revertToOriginal,
    applyOperation,

    // Image operations
    crop,
    resize,
    changeQuality,
    flip,
    brightnessContrast,
    sharpen,
    vignette,
    redEye,
    blemishRemove,
    backgroundRemove,
    penToolClip,
    skinEnhance,
    colorBalance,
    curves,

    // Special
    skinEnhanceVariants,
    save,
    download,
  };
}
